import { createSelector } from 'redux-orm';

import orm from '../orm';

const isActiveCardForListReport = (listModel, cardModel) => {
  if (!listModel || !cardModel) {
    return false;
  }

  if (listModel.is_archive == true || cardModel.is_archive == true) {
    return false;
  }

  const boardModel = listModel.board;
  if (boardModel?.is_archive == true) {
    return false;
  }

  return String(cardModel.listId) === String(listModel.id);
};

const getActiveCardModelsForList = (session, listId) => {
  const { List, Card } = session;
  const listModel = List.withId(listId);

  if (!listModel || listModel.is_archive == true) {
    return [];
  }

  const boardModel = listModel.board;
  if (boardModel?.is_archive == true) {
    return [];
  }

  return Card.all()
    .filter({ listId })
    .toModelArray()
    .filter((cardModel) => isActiveCardForListReport(listModel, cardModel));
};

export const selectCardPointDataForCardAndPointSystem = createSelector(
  orm,
  (_, cardId) => cardId,
  (_, __, pointSystemId) => pointSystemId,
  ({ CardPointData }, cardId, pointSystemId) => {
    if (!cardId || !pointSystemId) {
      return null;
    }

    const record = CardPointData.all()
      .filter({ cardId, pointSistemId: pointSystemId })
      .first();

    return record ? record.ref : null;
  },
);

export const makeSelectUnclosedCardPointDataForListAndCurrentMonth = () =>
  createSelector(
    orm,
    (_, listId) => listId,
    (_, __, pointSystemId) => pointSystemId,
    ({ List, Card, CardPointData }, listId, pointSystemId) => {
      if (!listId || !pointSystemId) {
        return [];
      }

      const listModel = List.withId(listId);
      if (!listModel || listModel.is_archive == true) {
        return [];
      }

      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth();

      const cardModels = getActiveCardModelsForList({ List, Card }, listId);
      const unclosedTasks = [];

      cardModels.forEach((cardModel) => {
        const cardPointDataModel = CardPointData.all()
          .filter({ cardId: cardModel.id, pointSistemId: pointSystemId })
          .first();

        // Задача незакрыта если:
        // 1. Нет записи в CardPointData
        // 2. Есть CardPointData и closed = false
        const isUnclosed = !cardPointDataModel || cardPointDataModel.closed === false;

        if (isUnclosed) {
          // Используем createdAt из CardPointData, если есть, иначе из Card
          const createdAt = cardPointDataModel
            ? (cardPointDataModel.ref.createdAt || cardModel.ref?.createdAt)
            : cardModel.ref?.createdAt;

          if (createdAt) {
            const taskDate = new Date(createdAt);
            const taskYear = taskDate.getFullYear();
            const taskMonth = taskDate.getMonth();

            if (taskYear === currentYear && taskMonth === currentMonth) {
              unclosedTasks.push({
                cardId: cardModel.id,
                cardName: cardModel.name,
                cardPointData: cardPointDataModel ? cardPointDataModel.ref : null,
              });
            }
          }
        }
      });

      return unclosedTasks;
    },
  );

export const selectUnclosedCardPointDataForListAndCurrentMonth = makeSelectUnclosedCardPointDataForListAndCurrentMonth();

export const makeSelectUnclosedCardPointDataForListAndMonth = () =>
  createSelector(
    orm,
    (_, listId) => listId,
    (_, __, pointSystemId) => pointSystemId,
    (_, __, ___, year) => year,
    (_, __, ___, ____, month) => month,
    ({ List, Card, CardPointData, ContentCardDefault, CardPointAdvancedData }, listId, pointSystemId, year, month) => {
      if (!listId || !pointSystemId || !year || !month) {
        return [];
      }

      const listModel = List.withId(listId);
      if (!listModel || listModel.is_archive == true) {
        return [];
      }

      const cardModels = getActiveCardModelsForList({ List, Card }, listId);
      const unclosedTasks = [];

      cardModels.forEach((cardModel) => {
        const cardPointDataModel = CardPointData.all()
          .filter({ cardId: cardModel.id, pointSistemId: pointSystemId })
          .first();

        // Задача незакрыта если:
        // 1. Нет записи в CardPointData
        // 2. Есть CardPointData и closed = false
        const isUnclosed = !cardPointDataModel || cardPointDataModel.closed === false;

        if (isUnclosed) {
          // Используем createdAt из CardPointData, если есть, иначе из Card
          const createdAt = cardPointDataModel
            ? (cardPointDataModel.ref.createdAt || cardModel.ref?.createdAt)
            : cardModel.ref?.createdAt;

          if (createdAt) {
            const taskDate = new Date(createdAt);
            const taskYear = taskDate.getFullYear();
            const taskMonth = taskDate.getMonth() + 1; // getMonth() возвращает 0-11, нужно 1-12

            if (taskYear === year && taskMonth === month) {
              // Получаем ContentCardDefault (только если есть CardPointData)
              const contentCardDefaultModel = cardPointDataModel?.contentCardDefault;
              const contentCardDefault = contentCardDefaultModel ? contentCardDefaultModel.ref : null;

              // Получаем CardPointAdvancedData
              const cardPointAdvancedDataModels = cardPointDataModel
                ? CardPointAdvancedData.all()
                    .filter({ cardId: cardModel.id, pointSistemId: pointSystemId })
                    .toRefArray()
                : [];

              unclosedTasks.push({
                cardId: cardModel.id,
                cardName: cardModel.name,
                cardPointData: cardPointDataModel ? cardPointDataModel.ref : null,
                card: cardModel.ref,
                contentCardDefault,
                cardPointAdvancedData: cardPointAdvancedDataModels,
              });
            }
          }
        }
      });

      return unclosedTasks;
    },
  );

export const selectUnclosedCardPointDataForListAndMonth = makeSelectUnclosedCardPointDataForListAndMonth();

// Селектор для всех незакрытых задач в списке (без фильтрации по году/месяцу)
// В секцию попадают все задачи, у которых:
// 1. Нет CardPointData
// 2. CardPointData.closed != true (т.е. closed !== true, включая false, null, undefined)
export const makeSelectAllUnclosedCardPointDataForList = () =>
  createSelector(
    orm,
    (_, listId) => listId,
    (_, __, pointSystemId) => pointSystemId,
    ({ List, Card, CardPointData }, listId, pointSystemId) => {
      if (!listId || !pointSystemId) {
        return [];
      }

      const listModel = List.withId(listId);
      if (!listModel || listModel.is_archive == true) {
        return [];
      }

      const cardModels = getActiveCardModelsForList({ List, Card }, listId);
      const unclosedTasks = [];

      cardModels.forEach((cardModel) => {
        const cardPointDataModel = CardPointData.all()
          .filter({ cardId: cardModel.id, pointSistemId: pointSystemId })
          .first();

        // Задача незакрыта если:
        // 1. Нет записи в CardPointData
        // 2. Есть CardPointData и closed != true (т.е. closed !== true)
        const isUnclosed = !cardPointDataModel || cardPointDataModel.closed !== true;

        if (isUnclosed) {
          unclosedTasks.push({
            cardId: cardModel.id,
            cardName: cardModel.name,
            cardPointData: cardPointDataModel ? cardPointDataModel.ref : null,
            card: cardModel.ref,
          });
        }
      });

      return unclosedTasks;
    },
  );

export const selectAllUnclosedCardPointDataForList = makeSelectAllUnclosedCardPointDataForList();

export const makeSelectCardIdsCurrentlyInList = () =>
  createSelector(
    orm,
    (_, listId) => listId,
    (session, listId) => {
      if (!listId) {
        return new Set();
      }

      const cardIds = getActiveCardModelsForList(session, listId).map((cardModel) => String(cardModel.id));

      return new Set(cardIds);
    },
  );

export const selectCardIdsCurrentlyInList = makeSelectCardIdsCurrentlyInList();

export default {
  selectCardPointDataForCardAndPointSystem,
  makeSelectUnclosedCardPointDataForListAndCurrentMonth,
  selectUnclosedCardPointDataForListAndCurrentMonth,
  makeSelectUnclosedCardPointDataForListAndMonth,
  selectUnclosedCardPointDataForListAndMonth,
  makeSelectAllUnclosedCardPointDataForList,
  selectAllUnclosedCardPointDataForList,
  makeSelectCardIdsCurrentlyInList,
  selectCardIdsCurrentlyInList,
};

